import React from 'react';
import {StyleSheet, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import CCText from '@/Atoms/CCText';
import {lbColors, lbRadius} from '../leaderboardTheme';
import {BannerVariant} from '../engagementPresentation';

interface Props {
  variant: BannerVariant;
  text: string;
  // Renders a progress bar toward the next milestone when provided.
  progressPct?: number;
  progressLeftLabel?: string;
  progressRightLabel?: string;
  // Smaller padding/fonts — used inside the header streak dropdown.
  compact?: boolean;
}

const VARIANT_META: Record<
  BannerVariant,
  {icon: string; bg: string; textColor: string}
> = {
  fresh: {
    icon: '🔥',
    bg: lbColors.bannerGradientStart,
    textColor: lbColors.goldInk,
  },
  progress: {
    icon: '🎁',
    bg: lbColors.bannerGradientStart,
    textColor: lbColors.goldInk,
  },
  oneAway: {
    icon: '⚡',
    bg: lbColors.bannerGradientStart,
    textColor: lbColors.goldInk,
  },
  eligible: {icon: '🎉', bg: lbColors.greenBg, textColor: lbColors.greenInk},
  atRisk: {icon: '⚠️', bg: lbColors.riskBg, textColor: lbColors.riskText},
};

/**
 * Milestone progress banner — reused by the Engagement page's path-to-rewards
 * strip, the dashboard preview card, and the header streak dropdown so the
 * three surfaces render identical copy/state.
 */
export default function MilestoneBanner({
  variant,
  text,
  progressPct,
  progressLeftLabel,
  progressRightLabel,
  compact = false,
}: Props) {
  const meta = VARIANT_META[variant];
  const isGradient =
    variant === 'fresh' || variant === 'progress' || variant === 'oneAway';

  const content = (
    <>
      <View style={styles.row}>
        <CCText fontSize={compact ? 15 : 19}>{meta.icon}</CCText>
        <CCText
          fontSize={compact ? 12.5 : 13.5}
          fontWeight="bold"
          color={meta.textColor}
          style={styles.text}>
          {text}
        </CCText>
      </View>
      {progressPct != null ? (
        <>
          <View style={[styles.track, compact && styles.trackCompact]}>
            <LinearGradient
              colors={[lbColors.orange, lbColors.gold]}
              start={{x: 0, y: 0}}
              end={{x: 1, y: 0}}
              style={[styles.fill, {width: `${Math.max(2, progressPct)}%`}]}
            />
          </View>
          {progressLeftLabel != null || progressRightLabel != null ? (
            <View style={styles.progressLabels}>
              <CCText
                fontSize={compact ? 9.5 : 10.5}
                fontWeight="bold"
                color={meta.textColor}>
                {progressLeftLabel}
              </CCText>
              <CCText
                fontSize={compact ? 9.5 : 10.5}
                fontWeight="bold"
                color={meta.textColor}>
                {progressRightLabel}
              </CCText>
            </View>
          ) : null}
        </>
      ) : null}
    </>
  );

  if (isGradient) {
    return (
      <LinearGradient
        colors={[lbColors.bannerGradientStart, lbColors.bannerGradientEnd]}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 1}}
        style={[styles.banner, compact && styles.bannerCompact]}>
        {content}
      </LinearGradient>
    );
  }

  return (
    <View
      style={[
        styles.banner,
        compact && styles.bannerCompact,
        {backgroundColor: meta.bg},
      ]}>
      {content}
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {borderRadius: lbRadius.md, padding: 14},
  bannerCompact: {borderRadius: lbRadius.sm + 2, padding: 10},
  row: {flexDirection: 'row', alignItems: 'center', gap: 8},
  text: {flex: 1, lineHeight: 17},
  track: {
    height: 9,
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.75)',
    marginTop: 10,
    overflow: 'hidden',
  },
  trackCompact: {height: 7, marginTop: 7},
  fill: {height: '100%', borderRadius: 6},
  progressLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 5,
  },
});
